import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { PageWrapper } from "@/components/layout/PageWrapper";

export default function Unsubscribe() {
  const { referentId } = useParams();
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get('campaign');
  
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customReason, setCustomReason] = useState('');
  const [selectedReason, setSelectedReason] = useState('no_longer_interested');

  const predefinedReasons = [
    { value: 'no_longer_interested', label: 'Je ne souhaite pas donner de référence' },
    { value: 'dont_know_candidate', label: 'Je ne connais pas suffisamment cette personne' },
    { value: 'too_busy', label: 'Je n\'ai pas le temps actuellement' },
    { value: 'inappropriate_request', label: 'Cette demande me semble inappropriée' },
    { value: 'other', label: 'Autre raison (précisez ci-dessous)' }
  ];

  const handleUnsubscribe = async () => {
    if (!referentId) {
      setError('Identifiant de référent manquant');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const reason = selectedReason === 'other' ? customReason : selectedReason;
      
      // Appeler l'edge function pour gérer le désabonnement
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/handle-unsubscribe?referent_id=${referentId}&campaign_id=${campaignId}&reason=${encodeURIComponent(reason)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        setCompleted(true);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Erreur lors du désabonnement');
      }
    } catch (err: any) {
      // Log error for monitoring but don't expose details to user
      setError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (completed) {
    return (
      <PageWrapper layout="no-sidebar">
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <CardTitle className="text-green-700">Désabonnement confirmé</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-muted-foreground">
                Votre demande de désabonnement a été traitée avec succès.
              </p>
              <p className="text-sm text-muted-foreground">
                Vous ne recevrez plus d'emails concernant cette demande de référence.
              </p>
            </CardContent>
          </Card>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper layout="no-sidebar">
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              Se désabonner des emails de référence
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="text-sm text-muted-foreground">
              <p>Vous ne souhaitez plus recevoir d'emails concernant cette demande de référence ?</p>
              <p className="mt-2">Merci de nous indiquer la raison pour nous aider à améliorer nos services.</p>
            </div>

            {error && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                <p className="text-destructive text-sm">{error}</p>
              </div>
            )}

            <div className="space-y-3">
              <label className="text-sm font-medium">Raison du désabonnement :</label>
              {predefinedReasons.map((reason) => (
                <label key={reason.value} className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="reason"
                    value={reason.value}
                    checked={selectedReason === reason.value}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    className="mt-0.5"
                  />
                  <span className="text-sm">{reason.label}</span>
                </label>
              ))}
            </div>

            {selectedReason === 'other' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Précisez votre raison :</label>
                <Textarea
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Merci de préciser votre raison..."
                  rows={3}
                />
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => window.history.back()}
                disabled={loading}
              >
                Annuler
              </Button>
              <Button
                onClick={handleUnsubscribe}
                disabled={loading || (selectedReason === 'other' && !customReason.trim())}
                className="flex-1"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Traitement...
                  </>
                ) : (
                  'Confirmer le désabonnement'
                )}
              </Button>
            </div>

            <div className="text-xs text-muted-foreground text-center pt-4 border-t">
              Si vous changez d'avis, vous pouvez toujours répondre en contactant directement la personne qui vous a sollicité.
            </div>
          </CardContent>
        </Card>
      </div>
    </PageWrapper>
  );
}